/**
 * MOCK DATA CONTRACT — NO BACKEND YET
 *
 * These types describe the per-luminaire analytics object used by the
 * admin-only Manufacturer Analytics dashboard. When the backend is built,
 * every field below becomes a real metric computed from TAMAN events
 * (matches, applies, favorites, inquiries).
 */

export type ProjectType =
  | 'road'
  | 'sidewalk'
  | 'pedestrian'
  | 'park'
  | 'parking_lot'
  | 'intersection'
  | 'roundabout'
  | 'highway'
  | 'square'
  | 'tunnel';

export const PROJECT_TYPES: ProjectType[] = [
  'road',
  'sidewalk',
  'pedestrian',
  'park',
  'parking_lot',
  'intersection',
  'roundabout',
  'highway',
  'square',
  'tunnel',
];

export interface MonthlyAnalyticsPoint {
  month: string; // ISO YYYY-MM
  compatibilityScoreAvg: number;
  matchFrequencyTotal: number;
  applyCount: number;
  favoriteCount: number;
  inquiriesGenerated: number;
}

export interface LuminaireAnalytics {
  // === Compatibility & matching ===
  /** 0–100, avg match score vs TAMAN optimal across all projects in date range */
  compatibilityScoreAvg: number;
  /** 90th percentile of match scores (ceiling performance) */
  compatibilityScoreP90: number;
  /** 0–100, mean deviation from optimal distribution (lower = better; INVERTED on radar) */
  optimalGapPercent: number;

  // === Usage ===
  /** Total appearances in Market Match top-N during period */
  matchFrequencyTotal: number;
  /** Normalized monthly rate */
  matchFrequencyPerMonth: number;
  /** Times this luminaire appeared in the top-3 Market Match results (high-intent surface) */
  top3MatchCount: number;
  /** Times this luminaire appeared in the top-100 Market Match results (broad surface) */
  top100MatchCount: number;
  /** Times planner clicked "Apply" */
  applyCount: number;
  /** applyCount / matchFrequencyTotal × 100 */
  applyRatePercent: number;
  /** Times added to favorites */
  favoriteCount: number;
  /** favoriteCount / matchFrequencyTotal × 100 */
  favoriteRatePercent: number;

  // === Spread & coverage ===
  projectTypesCovered: ProjectType[];
  projectTypeCount: number;
  regionsActive: string[]; // ISO country codes
  regionsCount: number;

  // === Commercial ===
  inquiriesGenerated: number;
  leadConversionRatePercent: number;

  // === Time series (for sparklines, 12 months) ===
  monthlyTimeSeries: MonthlyAnalyticsPoint[];

  // === Metadata ===
  lastMatchedAt: string | null;
  firstSeenAt: string;
}

/** Mock luminaire — extends a realistic ProductVariant-like shape with analytics. */
export interface MockLuminaire {
  id: string;
  sku: string; // variant code, e.g. "EH27"
  /** Display name, e.g. "Lavinia EH27" */
  name: string;
  /** Manufacturer / brand, e.g. "IGUZZINI" */
  manufacturer: string;
  /** Series slug used for filter + image lookup, e.g. "lavinia" */
  seriesId: string;
  /** Human series name, e.g. "Lavinia" */
  series: string;
  /** Product line within the series, e.g. "Pole mounted 617x333mm" */
  product: string;
  opticCode: string;
  power: number; // W
  lumenOutput: number; // lm
  colorTemperature: number; // K
  cri: number;
  ipRating: string;
  thumbnailColor: string; // fallback gradient tint when image fails to load
  /** Public-folder path to the series image, e.g. "/lamps/lavinia.png" */
  imageUrl: string;
  /** Public-folder path to the IES photometric file, when available for this SKU. */
  iesUrl?: string;
  segment: 'flagship' | 'workhorse' | 'niche' | 'underperformer' | 'new';
  analytics: LuminaireAnalytics;
}

type RadarAxisKey =
  | 'compatibility'
  | 'matchFrequency'
  | 'applyRate'
  | 'favoriteRate'
  | 'projectCoverage'
  | 'leadConversion'
  | 'optimalGap'
  | 'regionalSpread';

export interface RadarAxisDef {
  key: RadarAxisKey;
  /** Display label */
  label: string;
  /** Short description shown in tooltip / help panel */
  description: string;
  /** Unit suffix for the raw value (%, /mo, count, etc.) */
  unit: string;
  /** Whether the radar value is inverted (i.e., raw "lower is better") */
  inverted: boolean;
  /** Normalize raw value → 0..100 for the radar */
  normalize: (analytics: LuminaireAnalytics) => number;
  /** Return the raw value to show in tooltips / numeric table */
  rawValue: (analytics: LuminaireAnalytics) => number;
  /** Format raw value to display string */
  format: (raw: number) => string;
}

export type ComparisonMode = 'product' | 'optimal' | 'portfolioAvg' | 'marketAvg';

export type AnalyticsSubTab =
  | 'my-luminaires'
  | 'luminaire-performance'
  | 'overview'
  | 'portfolio-gap'
  | 'regional'
  | 'lead-activity';
